/**
 * Clinic record read — GET /v1/clinic (verified against cms-backend
 * src/clinic/clinic.service.ts CLINIC_VIEW_SELECT). Settings drive
 * calendar/currency/timezone presentation product-wide.
 *
 * ZOY-608 adds the read-only entry `slug`; ZOY-616 adds the logo FACTS (never
 * a URL) plus a separate member read that mints a short-lived presigned GET.
 */

import { apiRequest } from '../api/http';

export type CalendarSystem = 'jalali' | 'gregorian' | (string & {});

/** ZOY-616: what the platform stored. The view never carries a URL. */
export interface ClinicLogoFacts {
  uploadId: string;
  contentType: string;
  sizeBytes: number;
  width: number | null;
  height: number | null;
  updatedAt: string;
}

export interface ClinicView {
  clinicId: string;
  displayName: string | null;
  /** ZOY-608: the entry alias for /c/<slug>; null when the platform has not set one. */
  slug?: string | null;
  /** ZOY-616: logo facts, or null when the clinic has none. */
  logo?: ClinicLogoFacts | null;
  address: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  operatingContext: string | null;
  /** Canonical absolute URL (the server adds https:// to a bare host), or null. */
  website: string | null;
  /** Bare Instagram handle (no @); link = https://instagram.com/<handle>. */
  instagram: string | null;
  settings: {
    calendarSystem: CalendarSystem;
    currencyCode: string;
    defaultBufferMinutes: number;
    /** ZOY-252: minutes EACH WAY applied to at-home bookings that leave travel empty (0..480). */
    defaultTravelAllowanceMinutes: number;
    timezone: string;
    /**
     * tasks#138: the evening practitioner schedule SMS switch (server default
     * false). Optional because an older backend omits it — the toggle is then
     * hidden, never guessed. Never default it to true.
     */
    practitionerScheduleSms?: boolean;
  };
}

export function fetchClinic(clinicIdOverride?: string): Promise<ClinicView> {
  return apiRequest<ClinicView>({
    base: 'clinic',
    path: '/v1/clinic',
    clinicIdOverride,
  });
}

export function clinicDisplayName(c: Pick<ClinicView, 'displayName' | 'clinicId'>): string {
  return c.displayName ?? c.clinicId;
}

export interface ClinicLogoRead extends ClinicLogoFacts {
  /** Short-lived presigned GET, minted per request — never stored, never logged. */
  url: string;
  expiresAt: string;
}

export const ClinicLogoErrorCode = {
  NotSet: 'CLINIC_LOGO_NOT_SET',
} as const;

/**
 * ZOY-616: the selected clinic's logo for any active member. `404
 * CLINIC_LOGO_NOT_SET` when there is none — a fact, not a failure: the shell
 * renders the clinic's initials instead.
 */
export function fetchClinicLogo(): Promise<ClinicLogoRead> {
  return apiRequest<ClinicLogoRead>({ base: 'clinic', path: '/v1/clinic/logo' });
}
